import React from 'react'
import styled, { keyframes } from 'styled-components'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import homeCharacterImg from '../assets/Images/home-character.png'
import PixelSprite from '../pixel/PixelSprites'
import { PixelButton, PixelPanel, pixelCorners, pixelShadow } from '../pixel/pixelUI'

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`

const blink = keyframes`
  0%, 45% { opacity: 1; }
  46%, 100% { opacity: 0; }
`

const MainContainer = styled.main`
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8rem 2rem 8.5rem;
  background:
    radial-gradient(${(props) => props.theme.black}16 1.5px, transparent 1.5px),
    linear-gradient(180deg, ${(props) => props.theme.bubbles}30, transparent 58%),
    ${(props) => props.theme.body};
  background-size: 16px 16px, auto, auto;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 18%;
    background:
      linear-gradient(90deg, transparent 0 4%, ${(props) => props.theme.blossom}33 4% 11%, transparent 11% 15%, ${(props) => props.theme.bubbles}33 15% 25%, transparent 25% 31%, ${(props) => props.theme.buttercup}33 31% 43%, transparent 43% 48%, ${(props) => props.theme.lavender}44 48% 60%, transparent 60% 66%, ${(props) => props.theme.yellow}55 66% 78%, transparent 78% 84%, ${(props) => props.theme.blossom}33 84% 96%, transparent 96%),
      linear-gradient(transparent 74%, ${(props) => props.theme.black} 74% 78%, ${(props) => props.theme.buttercup} 78%);
    clip-path: polygon(0 70%, 4% 70%, 4% 40%, 11% 40%, 11% 58%, 15% 58%, 15% 24%, 25% 24%, 25% 54%, 31% 54%, 31% 34%, 43% 34%, 43% 64%, 48% 64%, 48% 18%, 60% 18%, 60% 56%, 66% 56%, 66% 30%, 78% 30%, 78% 63%, 84% 63%, 84% 40%, 96% 40%, 96% 70%, 100% 70%, 100% 100%, 0 100%);
    opacity: 0.7;
  }

  @media (max-width: 700px) {
    padding: 10rem 1rem 5rem;
    align-items: flex-start;
  }
`

const Hud = styled(motion.aside)`
  position: absolute;
  z-index: 4;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  gap: 0.7rem;

  @media (max-width: 700px) {
    top: 6rem;
    left: 1rem;
    right: 1rem;
    justify-content: center;
  }
`

const HudItem = styled.div`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.52rem;
  background: #fff;
  border: 3px solid ${(props) => props.theme.black};
  padding: 0.55rem 0.7rem;
  ${pixelCorners(4)}
  ${(props) => pixelShadow(3, 3, props.$color)}

  span { color: ${(props) => props.$color}; }

  @media (max-width: 420px) {
    font-size: 0.43rem;
    padding: 0.5rem;
  }
`

const Content = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1180px;
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(360px, 0.88fr);
  gap: clamp(2rem, 5vw, 5rem);
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 650px;
  }
`

const DialoguePanel = styled(PixelPanel)`
  overflow: hidden;
`

const WindowBar = styled.div`
  min-height: 2.6rem;
  padding: 0.6rem 0.9rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(props) => props.theme.blossom};
  border-bottom: 4px solid ${(props) => props.theme.black};
  color: #fff;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.55rem;
`

const WindowDots = styled.span`
  letter-spacing: 0.35rem;
  color: ${(props) => props.theme.black};
`

const DialogueBody = styled.div`
  padding: clamp(1.4rem, 4vw, 2.5rem);
`

const Eyebrow = styled(motion.p)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.56rem;
  color: ${(props) => props.theme.blossom};
  margin-bottom: 1.1rem;
`

const Title = styled(motion.h1)`
  font-size: clamp(2rem, 5.2vw, 4.2rem);
  line-height: 1.25;
  color: ${(props) => props.theme.black};
  margin-bottom: 1rem;

  span {
    display: block;
    color: ${(props) => props.theme.blossom};
    -webkit-text-stroke: 1.5px ${(props) => props.theme.black};
  }
`

const Subtitle = styled(motion.p)`
  font-size: clamp(1rem, 1.6vw, 1.18rem);
  font-weight: 650;
  line-height: 1.55;
  max-width: 47ch;
`

const MetaRow = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin: 1.4rem 0 1.7rem;
`

const MetaChip = styled.span`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.48rem;
  background: ${(props) => props.$bg};
  border: 2px solid ${(props) => props.theme.black};
  padding: 0.5rem 0.65rem;
`

const QuestLabel = styled(motion.p)`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.55rem;
  border-top: 3px dashed ${(props) => props.theme.black};
  padding-top: 1.1rem;
  margin-bottom: 1rem;

  &::before {
    content: '▶';
    margin-right: 0.65rem;
    color: ${(props) => props.theme.blossom};
    animation: ${blink} 1s steps(1) infinite;
  }
`

const ButtonRow = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 460px) {
    flex-direction: column;

    button { width: 100%; }
  }
`

const Stage = styled(motion.section)`
  position: relative;
`

const CharacterPanel = styled(PixelPanel)`
  overflow: hidden;
  background: ${(props) => props.theme.yellow};
`

const StageBar = styled.div`
  height: 2.6rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0.9rem;
  background: ${(props) => props.theme.black};
  color: #fff;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.52rem;
`

const Portrait = styled.div`
  min-height: 25rem;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 1.2rem 1.2rem 0;
  background:
    radial-gradient(circle at 22% 24%, #fff 0 4px, transparent 5px),
    radial-gradient(circle at 80% 18%, #fff 0 6px, transparent 7px),
    linear-gradient(180deg, ${(props) => props.theme.bubbles} 0 66%, #fff 66% 69%, ${(props) => props.theme.buttercup} 69%);

  img {
    position: relative;
    z-index: 2;
    width: 92%;
    max-height: 23rem;
    object-fit: contain;
    animation: ${float} 3.2s ease-in-out infinite;
  }

  @media (max-width: 460px) {
    min-height: 19rem;
    img { max-height: 18rem; }
  }
`

const PlayerTag = styled.div`
  position: absolute;
  z-index: 3;
  left: 1rem;
  top: 1rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.48rem;
  line-height: 1.7;
  padding: 0.65rem 0.75rem;
  background: #fff;
  border: 3px solid ${(props) => props.theme.black};
  ${(props) => pixelShadow(4, 4, props.theme.blossom)}

  strong { color: ${(props) => props.theme.blossom}; }
`

const HpBar = styled.div`
  position: absolute;
  z-index: 3;
  right: 1rem;
  bottom: 1rem;
  width: 8.5rem;
  padding: 0.55rem;
  background: #fff;
  border: 3px solid ${(props) => props.theme.black};
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.42rem;

  div {
    height: 0.65rem;
    margin-top: 0.4rem;
    border: 2px solid ${(props) => props.theme.black};
    background: linear-gradient(90deg, ${(props) => props.theme.buttercup} 0 88%, ${(props) => props.theme.body} 88%);
  }
`

const FloatSprite = styled.div`
  position: absolute;
  z-index: 4;
  ${(props) => props.$left && `left: ${props.$left};`}
  ${(props) => props.$right && `right: ${props.$right};`}
  ${(props) => props.$top && `top: ${props.$top};`}
  animation: ${float} ${(props) => props.$duration || 3}s ease-in-out infinite;
`

const textContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 280, damping: 24 } },
}

const Main = () => {
  const navigate = useNavigate()

  return (
    <MainContainer>
      <Hud initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
        <HudItem $color="#FF3E8E">PLAYER <span>HERMIA</span></HudItem>
        <HudItem $color="#4FC9FF">BASE <span>VANCOUVER</span></HudItem>
        <HudItem $color="#8CE04A">STATUS <span>ONLINE</span></HudItem>
      </Hud>

      <Content>
        <DialoguePanel as={motion.section} variants={textContainer} initial="hidden" animate="show">
          <WindowBar>
            <span>PLAYER_PROFILE.EXE</span>
            <WindowDots>■ ■ ■</WindowDots>
          </WindowBar>
          <DialogueBody>
            <Eyebrow variants={fadeUp}>
              <PixelSprite name="sparkle" size={3} color="#FF3E8E" />
              SOFTWARE ENGINEER
            </Eyebrow>
            <Title variants={fadeUp}>Hermia <span>Tian</span></Title>
            <Subtitle variants={fadeUp}>
              I turn real-world problems into playful, useful digital experiences —
              from mobile products to web apps and games.
            </Subtitle>
            <MetaRow variants={fadeUp}>
              <MetaChip $bg="#FF3E8E">CREATIVE CODER</MetaChip>
              <MetaChip $bg="#4FC9FF">PRODUCT BUILDER</MetaChip>
              <MetaChip $bg="#8CE04A">PROBLEM SOLVER</MetaChip>
            </MetaRow>
            <QuestLabel variants={fadeUp}>CHOOSE YOUR NEXT STAGE</QuestLabel>
            <ButtonRow variants={fadeUp}>
              <PixelButton $bg="#FF3E8E" style={{ color: '#fff' }} onClick={() => navigate('/work')}>
                SELECT PROJECT
              </PixelButton>
              <PixelButton $bg="#4FC9FF" onClick={() => navigate('/skills')}>
                VIEW LOADOUT
              </PixelButton>
            </ButtonRow>
          </DialogueBody>
        </DialoguePanel>

        <Stage
          initial={{ opacity: 0, scale: 0.75, x: 30 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ type: 'spring', stiffness: 180, damping: 18, delay: 0.2 }}
        >
          <FloatSprite $left="-1rem" $top="12%" $duration={2.7}>
            <PixelSprite name="heart" size={5} color="#FF3E8E" />
          </FloatSprite>
          <FloatSprite $right="0" $top="17%" $duration={3.3}>
            <PixelSprite name="star" size={5} color="#FFD93D" />
          </FloatSprite>
          <CharacterPanel>
            <StageBar><span>STAGE 00</span><span>READY!</span></StageBar>
            <Portrait>
              <PlayerTag><strong>LV. 05</strong><br />FULL-STACK HERO</PlayerTag>
              <img src={homeCharacterImg} alt="Pixel-art portrait of Hermia taking a selfie" />
              <HpBar>BUILD ENERGY<div /></HpBar>
            </Portrait>
          </CharacterPanel>
        </Stage>
      </Content>
    </MainContainer>
  )
}

export default Main
